import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCreateNote,
  noteSortDirectionFor,
  searchNoteIdsByContent,
  sortNotes,
  useDeleteNote,
  useNote,
  useNoteTitles,
  usePinNote,
} from "../hooks/useNotes";
import { useArchiveNote, useUnarchiveNote } from "../hooks/useArchive";
import { useSignInGate } from "../hooks/useSignInGate";
import { searchSharedNotes } from "../hooks/useSharedWithMe";
import { getSharingFeature } from "../lib/noteEngineState";
import { compareTagHandlesPinnedFirst, useTags } from "../hooks/useTags";
import { useRestoreNote } from "../hooks/useTrash";
import { getNote } from "../lib/db";
import { parseSearchQuery, searchSnippet } from "@caliu/utils";
import { matchSegments } from "../lib/matchHighlight";
import { getNotePreview } from "../lib/noteContent";
import { setPrefetchedNote } from "../lib/notePrefetch";
import { newNoteContent } from "../lib/newNoteContent";
import { extractTitleFromContent } from "../hooks/useEditorAutosave";
import { formatKeyCombo } from "../lib/shortcuts";
import { tagFullPath } from "../lib/tagHierarchy";
import { TagIcon } from "../lib/tagIcons";
import { cn } from "../lib/utils";
import { useShortcutsStore } from "../store/shortcuts";
import { useUIStore } from "../store/ui";
import { AnalyticsEvent, track } from "../lib/analytics";
import { Icon } from "@/components/ui/Icon";
import { isSharedWithMe } from "../lib/sharing";
import { useAuthStore } from "../store/auth";
import { LeaveNoteDialog } from "./sharing/LeaveNoteDialog";

/* ── Types ─────────────────────────────────────────── */

interface CommandItem {
  id: string;
  type: "note" | "tag" | "action" | "nav" | "note-action";
  label: string;
  subtitle?: string;
  icon: ReactNode;
  action: () => void;
  keywords?: string[];
  danger?: boolean;
  shortcut?: string;
}

/* ── Match highlighting ────────────────────────────── */

/** Renders `text` with every occurrence of each search term accented. */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {matchSegments(text, terms).map((seg, i) =>
        seg.match ? (
          <span key={i} className="text-accent font-semibold">
            {seg.text}
          </span>
        ) : (
          <Fragment key={i}>{seg.text}</Fragment>
        ),
      )}
    </>
  );
}

/* ── CommandPalette ────────────────────────────────── */

export function CommandPalette() {
  const { t } = useTranslation();
  const {
    cmdPaletteOpen: open,
    closeCmdPalette,
    activeNoteId,
    noteSortField,
  } = useUIStore();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isKeyboardNav = useRef(false);

  const allNoteTitles = useNoteTitles();
  const tags = useTags();
  const createNote = useCreateNote();

  const { note: activeNote } = useNote(activeNoteId);
  const isActiveNoteInTrash = !!activeNote?.deletedAt;
  const isActiveNoteArchived = !isActiveNoteInTrash && !!activeNote?.archivedAt;
  const pinNote = usePinNote();
  const deleteNote = useDeleteNote();
  const restoreNote = useRestoreNote();
  const archiveNoteHook = useArchiveNote();
  const unarchiveNoteHook = useUnarchiveNote();
  const openShareSheet = useUIStore((s) => s.openShareSheet);
  const meId = useAuthStore((s) => s.user?.id);
  // Leave outlives the palette (it closes first), so its dialog is held here.
  const [leaveNoteId, setLeaveNoteId] = useState<string | null>(null);
  const requireAccount = useSignInGate();
  const resolved = useShortcutsStore((s) => s.resolved);

  // Sync open state to visible/closing for exit animation
  useEffect(() => {
    if (open) {
      setClosing(false);
      setVisible(true);
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    } else if (visible) {
      setClosing(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setClosing(false);
      }, 120); // matches exit animation duration
      return () => clearTimeout(timer);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    closeCmdPalette();
    setQuery("");
    setActiveIndex(0);
  }, [closeCmdPalette]);

  /* ── Note actions (context-sensitive) ────────────── */

  const noteActions: CommandItem[] = useMemo(() => {
    if (!activeNoteId || !activeNote) return [];

    if (isActiveNoteInTrash) {
      return [
        {
          id: "note-restore",
          type: "note-action",
          label: t("commandPalette.restoreNote"),
          icon: <Icon name="restore" size={16} />,
          keywords: ["restore", "recover", "undelete", "trash"],
          action: () => {
            close();
            void restoreNote.mutate(activeNoteId).then(async () => {
              const localNote = getNote(activeNoteId);
              if (localNote) setPrefetchedNote(localNote);
              navigate(`/app/note/${activeNoteId}`);
            });
          },
        },
      ];
    }

    if (isActiveNoteArchived) {
      return [
        {
          id: "note-unarchive",
          type: "note-action",
          label: t("commandPalette.unarchiveNote"),
          icon: <Icon name="unarchive" size={16} />,
          keywords: ["unarchive", "restore", "archive"],
          action: () => {
            close();
            void unarchiveNoteHook.mutate(activeNoteId).then(() => {
              const localNote = getNote(activeNoteId);
              if (localNote) setPrefetchedNote(localNote);
              navigate(`/app/note/${activeNoteId}`);
            });
          },
        },
      ];
    }

    const actions: CommandItem[] = [];

    // Native palette order: Find in Note first.
    actions.push({
      id: "note-find",
      type: "note-action",
      label: t("toolbar.findInNote"),
      icon: <Icon name="search" size={16} />,
      keywords: ["find", "search", "replace"],
      shortcut: resolved["editor.find"],
      action: () => {
        close();
        window.dispatchEvent(new CustomEvent("caliu:open-find"));
      },
    });

    actions.push({
      id: "note-pin",
      type: "note-action",
      label: activeNote.isPinned ? t("commandPalette.unpinNote") : t("commandPalette.pinNote"),
      icon: <Icon name="pin" size={16} />,
      keywords: ["pin", "unpin", "favorite", "star"],
      action: () => {
        close();
        pinNote.mutate({ id: activeNoteId, isPinned: !activeNote.isPinned });
      },
    });

    if (activeNote.sharedAt) {
      const webUrl = import.meta.env.VITE_WEB_URL ?? "https://caliuapp.com";
      actions.push({
        id: "note-copy-link",
        type: "note-action",
        label: t("commandPalette.copyShareLink"),
        icon: <Icon name="duplicate" size={16} />,
        keywords: ["copy", "link", "share", "url"],
        action: () => {
          close();
          navigator.clipboard.writeText(`${webUrl}/share/${activeNoteId}`);
          track(AnalyticsEvent.ShareLinkCopied);
        },
      });
    }
    actions.push({
      id: "note-share",
      type: "note-action",
      label: t("noteActions.share"),
      icon: <Icon name="share" size={16} />,
      keywords: ["share", "link", "publish", "public", "invite", "people"],
      action: () => {
        // Same gate as NoteActionsMenu: sharing needs an account.
        if (requireAccount("share")) return;
        close();
        openShareSheet(activeNoteId, "command_palette");
      },
    });

    actions.push({
      id: "note-fullscreen",
      type: "note-action",
      label: t("commandPalette.toggleFullscreen"),
      icon: <Icon name="expand" size={16} />,
      keywords: ["fullscreen", "expand", "focus", "distraction"],
      shortcut: resolved["editor.fullscreen"],
      action: () => {
        close();
        window.dispatchEvent(new CustomEvent("caliu:toggle-fullscreen"));
      },
    });

    actions.push({
      id: "note-info",
      type: "note-action",
      label: t("commandPalette.noteInfo"),
      icon: <Icon name="info" size={16} />,
      keywords: ["info", "stats", "word count", "details", "metadata"],
      action: () => {
        close();
        window.dispatchEvent(new CustomEvent("caliu:open-note-info"));
      },
    });

    actions.push({
      id: "note-archive",
      type: "note-action",
      label: t("commandPalette.archiveNote"),
      icon: <Icon name="archive" size={16} />,
      keywords: ["archive", "stash", "hide"],
      action: () => {
        close();
        archiveNoteHook.mutate(activeNoteId);
        navigate("/app");
      },
    });

    if (isSharedWithMe(activeNote, meId)) {
      // A member's delete is Leave (PLAN.md D5): confirmed like the menu's.
      actions.push({
        id: "note-leave",
        type: "note-action",
        label: t("sharing.leaveNote"),
        icon: <Icon name="sign-out" size={16} />,
        keywords: ["leave", "delete", "remove", "trash"],
        danger: true,
        action: () => {
          close();
          setLeaveNoteId(activeNoteId);
        },
      });
    } else {
      actions.push({
        id: "note-trash",
        type: "note-action",
        label: t("commandPalette.moveToTrash"),
        icon: <Icon name="trash" size={16} />,
        keywords: ["delete", "remove", "trash", "bin"],
        danger: true,
        action: () => {
          close();
          deleteNote.mutate(activeNoteId);
          navigate("/app");
        },
      });
    }

    return actions;
  }, [
    activeNoteId,
    activeNote,
    meId,
    isActiveNoteInTrash,
    isActiveNoteArchived,
    close,
    pinNote,
    deleteNote,
    restoreNote,
    archiveNoteHook,
    unarchiveNoteHook,
    openShareSheet,
    requireAccount,
    navigate,
    resolved,
    t,
  ]);

  /* ── Build items ─────────────────────────────────── */

  const navCommands: CommandItem[] = [
    {
      id: "new-note",
      type: "action",
      label: t("commandPalette.newNote"),
      icon: <Icon name="note-new" size={16} />,
      keywords: ["create", "add", "write"],
      shortcut: resolved["app.newNote"],
      action: async () => {
        close();
        try {
          const content = newNoteContent(
            useUIStore.getState().newNoteStart,
            undefined,
            new Date(),
          );
          const note = await createNote.mutateAsync(
            { title: extractTitleFromContent(content), content },
            "command_palette",
          );
          // null: the free note limit is reached and the paywall is already open.
          if (!note) return;
          useUIStore.getState().setPendingNewNoteId(note.id);
          const localNote = getNote(note.id);
          if (localNote) setPrefetchedNote(localNote);
          navigate(`/app/note/${note.id}`);
        } catch {
          navigate("/app");
        }
      },
    },
    {
      id: "all-notes",
      type: "nav",
      label: t("commandPalette.allNotes"),
      icon: <Icon name="leaf" size={16} />,
      keywords: ["home", "notes"],
      action: () => {
        close();
        navigate("/app");
      },
    },
    {
      id: "today",
      type: "nav",
      label: t("commandPalette.todaysNotes"),
      icon: <Icon name="clock" size={16} />,
      keywords: ["recent", "daily"],
      action: () => {
        close();
        track(AnalyticsEvent.SmartViewSelected, {
          view: "today",
          source: "command_palette",
        });
        navigate("/app?view=today");
      },
    },
    {
      id: "todo",
      type: "nav",
      label: t("commandPalette.todo"),
      icon: <Icon name="check-circle" size={16} />,
      keywords: ["todo", "task", "checkbox", "pending"],
      action: () => {
        close();
        track(AnalyticsEvent.SmartViewSelected, {
          view: "todo",
          source: "command_palette",
        });
        navigate("/app?view=todo");
      },
    },
    {
      id: "pinned",
      type: "nav",
      label: t("commandPalette.pinnedNotes"),
      icon: <Icon name="pin" size={16} />,
      keywords: ["pin", "starred", "favorite"],
      action: () => {
        close();
        track(AnalyticsEvent.SmartViewSelected, {
          view: "pinned",
          source: "command_palette",
        });
        navigate("/app?view=pinned");
      },
    },
    {
      id: "archive",
      type: "nav",
      label: t("commandPalette.archive"),
      icon: <Icon name="archive" size={16} />,
      keywords: ["archive", "archived", "stash"],
      action: () => {
        close();
        track(AnalyticsEvent.SmartViewSelected, {
          view: "archive",
          source: "command_palette",
        });
        navigate("/app?view=archive");
      },
    },
    {
      id: "trash",
      type: "nav",
      label: t("commandPalette.trash"),
      icon: <Icon name="trash" size={16} />,
      keywords: ["deleted", "bin"],
      action: () => {
        close();
        track(AnalyticsEvent.SmartViewSelected, {
          view: "trash",
          source: "command_palette",
        });
        navigate("/app?view=trash");
      },
    },
    {
      id: "import",
      type: "action",
      label: t("commandPalette.importNotes"),
      icon: <Icon name="upload" size={16} />,
      keywords: ["upload", "markdown", "bear"],
      action: () => {
        close();
        window.dispatchEvent(new CustomEvent("caliu:open-import"));
      },
    },
    {
      id: "themes",
      type: "action",
      label: t("commandPalette.changeTheme"),
      icon: <Icon name="palette" size={16} />,
      keywords: ["theme", "themes", "dark", "light", "appearance", "color", "colors"],
      action: () => {
        close();
        useUIStore.getState().openSettings("theme");
      },
    },
    {
      id: "settings",
      type: "action",
      label: t("settings.title"),
      icon: <Icon name="sliders" size={16} />,
      keywords: ["settings", "preferences", "options", "account", "language"],
      shortcut: resolved["app.settings"],
      action: () => {
        close();
        useUIStore.getState().openSettings();
      },
    },
    {
      id: "shortcuts",
      type: "action",
      label: t("commandPalette.keyboardShortcuts"),
      icon: <Icon name="keyboard" size={16} />,
      keywords: ["shortcuts", "keys", "bindings", "hotkeys"],
      action: () => {
        close();
        useUIStore.getState().openSettings("shortcuts");
      },
    },
  ];

  const q = query.toLowerCase().trim();
  const parsedQuery = parseSearchQuery(query);
  const highlightTerms = parsedQuery.highlights;
  // Title matching reads the query as one literal: only for plain words, so a
  // `#tag`, `@special`, `-word`, `or` or `@title` query goes to the search alone.
  const isTextOnly =
    parsedQuery.groups.length === 1 &&
    !parsedQuery.groups[0].titleOnly &&
    parsedQuery.groups[0].terms.every((t) => t.kind === "text" && !t.negated);

  let items: CommandItem[] = [];

  if (!q) {
    items = [...noteActions, ...navCommands];
  } else {
    const matchedNoteActions = noteActions.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.keywords?.some((k) => k.toLowerCase().includes(q)),
    );
    const matchedCommands = navCommands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.keywords?.some((k) => k.toLowerCase().includes(q)),
    );

    // Filter notes — title matches first (cheap in-memory), then the search
    // query language via SQL. A `#tag` or `@special` must hold for every note
    // shown, which the title match knows nothing about: such queries go to SQL
    // alone (Swift's `performSearch` does the same).
    const titleMatches = !isTextOnly ? [] : allNoteTitles
      .filter((n) => n.title.toLowerCase().includes(q))
      .slice(0, 8);
    const titleIdSet = new Set(titleMatches.map((n) => n.id));
    const contentIds = titleMatches.length < 8
      ? searchNoteIdsByContent(q, titleIdSet, 8 - titleMatches.length)
      : [];
    const matchedIds = [...titleMatches.map((n) => n.id), ...contentIds];

    // Exact note-id lookup: pasting a note's identifier surfaces it directly
    // (the id is neither in the title nor the content, so nothing else matches).
    const rawQ = query.trim();
    if (rawQ && !matchedIds.includes(rawQ)) {
      const byId = getNote(rawQ);
      if (byId && !byId.deletedAt) {
        matchedIds.unshift(rawQ);
      }
    }

    // Shared-with-me notes are searchable too — they live in the
    // shared_notes cache, not the notes table.
    // Only while `sharing` is off: with it on they are rows of `notes`.
    const sharedMatches = (getSharingFeature() ? [] : searchSharedNotes(q, 4)).filter(
      (s) => !matchedIds.includes(s.id),
    );
    const sharedById = new Map(sharedMatches.map((s) => [s.id, s]));
    matchedIds.push(...sharedMatches.map((s) => s.id));

    // Relevance decides WHICH notes match (title hit, then content, then
    // shared); the user's note-list sort decides the order they are shown in,
    // so the palette and the list never disagree. Mirrors Swift's
    // CommandPaletteController.performSearch.
    const notesById = new Map(
      matchedIds.map((id) => [id, getNote(id) ?? sharedById.get(id)] as const),
    );
    const sortedIds = sortNotes(
      matchedIds.map((id) => notesById.get(id)).filter((n) => !!n),
      noteSortField,
      noteSortDirectionFor(noteSortField),
    ).map((n) => n.id);
    // Ids the notes/shared tables can't resolve (title-only rows) keep their
    // relevance slot at the end rather than vanishing.
    const orderedIds = [
      ...sortedIds,
      ...matchedIds.filter((id) => !notesById.get(id)),
    ];

    const matchedNotes: CommandItem[] = orderedIds.map((id) => {
      const note = getNote(id) ?? sharedById.get(id);
      const title = note?.title || titleMatches.find((n) => n.id === id)?.title || t("common.untitled");
      const content = note?.content ?? "";
      const preview = content ? getNotePreview(content) : "";
      // Window the subtitle around the first term so a body hit is visible
      // (and highlightable), not hidden behind the note's head. Prefer the
      // clean preview; if the hit was stripped from it (a #tag lives in the raw
      // markdown but getNotePreview removes hashtags), window the raw body
      // instead. Skip Tiptap-JSON notes (preview keeps tags).
      const rawBody = content && !content.trimStart().startsWith("{") ? content : "";
      const subtitle =
        searchSnippet(preview, highlightTerms, 300) ??
        searchSnippet(rawBody, highlightTerms, 300) ??
        (preview.slice(0, 300) || undefined);
      const isShared = sharedById.has(id);
      return {
        id: `note-${id}`,
        type: "note" as const,
        label: title,
        subtitle,
        icon: isShared ? <Icon name="users" size={16} /> : <Icon name="note" size={16} />,
        action: () => {
          close();
          // Shared stubs are not in the notes table — nothing to prefetch;
          // the route renders the shared viewer from the local cache.
          if (note && !isShared) setPrefetchedNote(note);
          navigate(`/app/note/${id}`);
        },
      };
    });

    // A tag row stores only its leaf segment, so match and label on the full
    // handle ("work/project") — a bare "project" is ambiguous across parents
    // and can't be found by typing the path the user sees in the sidebar.
    const tagsById = new Map(tags.map((t) => [t.id, t]));
    const matchedTags: CommandItem[] = tags
      .map((t) => ({ tag: t, handle: tagFullPath(t, tagsById) }))
      .filter(({ handle }) => handle.toLowerCase().includes(q))
      .sort(compareTagHandlesPinnedFirst)
      .slice(0, 5)
      .map(({ tag: t, handle }) => ({
        id: `tag-${t.id}`,
        type: "tag" as const,
        label: `#${handle}`,
        icon: t.icon ? (
          <TagIcon name={t.icon} size={14} />
        ) : t.color ? (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: t.color,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
        ) : (
          <Icon name="tag" size={16} />
        ),
        action: () => {
          close();
          navigate(`/app?tag=${t.id}`);
        },
      }));

    items = [
      ...matchedNoteActions,
      ...matchedCommands,
      ...matchedNotes,
      ...matchedTags,
    ];
  }

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /* ── Group items for rendering ───────────────────── */

  type GroupedItem =
    | { kind: "label"; text: string; id: string }
    | { kind: "item"; item: CommandItem; flatIndex: number };

  const grouped: GroupedItem[] = [];

  const currentNoteItems = items.filter((i) => i.type === "note-action");
  const navItems = items.filter((i) => i.type === "nav" || i.type === "action");
  const noteItems = items.filter((i) => i.type === "note");
  const tagItems = items.filter((i) => i.type === "tag");

  // `search_performed` from the surface users actually search on. Debounced so a
  // settled query reports once instead of once per keystroke, matching the
  // 150ms debounce on Swift's CommandPaletteController.
  const searchResultCount = noteItems.length + tagItems.length;
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const timer = setTimeout(() => {
      track(AnalyticsEvent.SearchPerformed, {
        scope: "command_palette",
        result_count: searchResultCount,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchResultCount]);

  let flatIndex = 0;

  if (currentNoteItems.length > 0) {
    grouped.push({
      kind: "label",
      text: t("commandPalette.currentNote"),
      id: "lbl-note-actions",
    });
    for (const item of currentNoteItems) {
      grouped.push({ kind: "item", item, flatIndex: flatIndex++ });
    }
  }
  if (navItems.length > 0) {
    grouped.push({ kind: "label", text: t("commandPalette.navigation"), id: "lbl-cmds" });
    for (const item of navItems) {
      grouped.push({ kind: "item", item, flatIndex: flatIndex++ });
    }
  }
  if (noteItems.length > 0) {
    grouped.push({ kind: "label", text: t("commandPalette.notes"), id: "lbl-notes" });
    for (const item of noteItems) {
      grouped.push({ kind: "item", item, flatIndex: flatIndex++ });
    }
  }
  if (tagItems.length > 0) {
    grouped.push({ kind: "label", text: t("commandPalette.tags"), id: "lbl-tags" });
    for (const item of tagItems) {
      grouped.push({ kind: "item", item, flatIndex: flatIndex++ });
    }
  }

  const totalItems = flatIndex;

  /* ── Keyboard navigation ─────────────────────────── */

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveIndex((i) => (i + 1) % Math.max(totalItems, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveIndex(
          (i) => (i - 1 + Math.max(totalItems, 1)) % Math.max(totalItems, 1),
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const activeItem = itemsRef.current[activeIndex];
        if (activeItem) activeItem.action();
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    },
    [activeIndex, totalItems, close],
  );

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const leaveDialog = leaveNoteId ? (
    <LeaveNoteDialog
      noteId={leaveNoteId}
      open
      onOpenChange={(o) => {
        if (!o) setLeaveNoteId(null);
      }}
      source="command_palette"
      onLeft={() => navigate("/app")}
    />
  ) : null;

  if (!visible) return leaveDialog;

  const animating = closing;

  return (
    <>
    {leaveDialog}
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]"
      style={{
        background: "var(--color-overlay)",
        animation: animating
          ? "backdropOut 120ms ease-in both"
          : "backdropIn 150ms ease-out both",
        pointerEvents: animating ? "none" : undefined,
      }}
      onClick={close}
    >
      <div
        className="bg-canvas border border-border-light flex flex-col overflow-hidden rounded-2xl font-chrome"
        style={{
          width: "min(560px, calc(100vw - 32px))",
          maxHeight: 420,
          boxShadow:
            "0 20px 60px var(--color-shadow-heavy), 0 4px 16px var(--color-shadow)",
          animation: animating
            ? "cmdPaletteOut 120ms ease-in both"
            : "cmdPaletteIn 150ms ease-out both",
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input row */}
        <div className="relative shrink-0">
          <Icon name="search"
            size={16}
            className="text-muted absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder={t("commandPalette.placeholder")}
            className="text-ink no-focus-ring w-full border-none bg-transparent font-chrome text-[16px] md:text-lg outline-none rounded-t-2xl py-4 pr-4 md:pr-20 pl-11 tracking-tight text-ellipsis placeholder:text-ellipsis"
          />
          <kbd className="hidden md:block text-muted bg-border absolute right-4 top-1/2 -translate-y-1/2 text-[11px] px-1.5 py-0.5 rounded tracking-wide pointer-events-none">
            {formatKeyCombo(resolved["app.commandPalette"] ?? "mod+k")}
          </kbd>
        </div>

        {/* Divider */}
        {totalItems > 0 && (
          <div className="bg-border h-px shrink-0" />
        )}

        {/* Results */}
        {totalItems > 0 && (
          <div
            ref={listRef}
            className="overflow-y-auto grow pt-1 px-1.5 pb-2"
          >
            {grouped.map((g) => {
              if (g.kind === "label") {
                return (
                  <div
                    key={g.id}
                    className="text-muted text-[10px] font-semibold tracking-widest uppercase px-2.5 pt-2 pb-1 font-chrome"
                  >
                    {g.text}
                  </div>
                );
              }

              const { item, flatIndex: idx } = g;
              const isActive = idx === activeIndex;
              const isDanger = item.danger === true;

              return (
                <button
                  key={item.id}
                  data-idx={idx}
                  onClick={item.action}
                  onMouseMove={() => {
                    isKeyboardNav.current = false;
                  }}
                  onMouseEnter={() => {
                    if (!isKeyboardNav.current) setActiveIndex(idx);
                  }}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-2.5 border-none rounded-full cursor-pointer text-left font-chrome text-[13px] transition-[background,color] duration-100 shrink-0",
                    item.subtitle ? "h-[52px]" : "h-[38px]",
                    isActive
                      ? isDanger
                        ? "text-danger bg-danger-bg"
                        : "bg-accent-bg text-accent-text"
                      : isDanger
                        ? "text-danger-muted"
                        : "text-ink",
                  )}
                >
                  <span
                    className={cn(
                      "shrink-0 flex items-center",
                      isActive
                        ? isDanger
                          ? "text-danger"
                          : "text-accent"
                        : isDanger
                          ? "text-danger-muted"
                          : "text-muted",
                    )}
                  >
                    {item.icon}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-[450]">
                      <Highlighted text={item.label} terms={highlightTerms} />
                    </span>
                    {item.subtitle && (
                      <span
                        className={cn(
                          "block text-[11px] truncate opacity-80",
                          isActive ? "text-accent-text" : "text-muted",
                        )}
                      >
                        <Highlighted text={item.subtitle} terms={highlightTerms} />
                      </span>
                    )}
                  </span>
                  {item.shortcut && (
                    <kbd className="text-muted bg-border text-[10px] px-1.5 py-0.5 rounded tracking-wide shrink-0 font-chrome">
                      {formatKeyCombo(item.shortcut)}
                    </kbd>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {totalItems === 0 && q && (
          <div className="text-muted px-5 py-6 text-center text-[13px] font-chrome">
            {t("commandPalette.noResults", { query })}
          </div>
        )}
      </div>

      {/* Animation keyframes injected via style tag */}
      <style>{`
        @keyframes cmdPaletteIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cmdPaletteOut {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to   { opacity: 0; transform: translateY(-6px) scale(0.98); }
        }
        @keyframes backdropIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes backdropOut { from { opacity: 1; } to { opacity: 0; } }
      `}</style>
    </div>
    </>
  );
}
